<template>
    <div class="compass-card accent-top">
        <h3 class="h5 text-gradient mb-3">Create DCA Order</h3>
        <p class="text-muted small mb-3">
            Dollar Cost Average into any token with automatic recurring purchases.
        </p>

        <!-- From Token -->
        <div class="mb-3">
            <label class="form-label small text-muted">Spend Token</label>
            <div class="dca-token-picker" @click="openSelector('from')">
                <img v-if="fromToken.logoURI" :src="fromToken.logoURI" class="dca-token-logo" alt="">
                <span class="dca-token-symbol">{{ fromToken.symbol || 'Select token' }}</span>
                <svg class="dca-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
        </div>

        <!-- To Token -->
        <div class="mb-3">
            <label class="form-label small text-muted">Buy Token</label>
            <div class="dca-token-picker" @click="openSelector('to')">
                <img v-if="toToken.logoURI" :src="toToken.logoURI" class="dca-token-logo" alt="">
                <span class="dca-token-symbol">{{ toToken.symbol || 'Select token' }}</span>
                <svg class="dca-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
        </div>

        <!-- Token Selector Modal -->
        <TokenSelectorModal
            v-model="showTokenSelector"
            :mode="tokenSelectorMode"
            :current-from-token="fromToken.mint"
            :current-to-token="toToken.mint"
            @select="handleTokenSelect"
        />

        <!-- Total Amount -->
        <div class="mb-3">
            <label class="form-label small text-muted">Total Amount</label>
            <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    v-model.number="totalAmount"
                    placeholder="0.00"
                    min="0"
                    step="any"
                >
                <span class="input-group-text" v-if="fromToken">{{ fromToken.symbol || 'Token' }}</span>
            </div>
        </div>

        <!-- Interval -->
        <div class="mb-3">
            <label class="form-label small text-muted">Frequency</label>
            <div class="d-flex gap-2">
                <button
                    v-for="opt in intervalOptions"
                    :key="opt.value"
                    class="compass-btn compass-btn--sm flex-fill"
                    :class="interval === opt.value ? 'compass-btn--primary' : 'compass-btn--outline'"
                    @click="interval = opt.value"
                >
                    {{ opt.label }}
                </button>
            </div>
        </div>

        <!-- Number of Orders -->
        <div class="mb-3">
            <label class="form-label small text-muted">Number of Orders</label>
            <input
                type="number"
                class="form-control"
                v-model.number="orderCount"
                placeholder="10"
                min="2"
                max="1000"
            >
            <div class="form-text small" v-if="amountPerOrder > 0">
                <span class="mono-num">{{ formatAmount(amountPerOrder) }}</span>
                {{ fromToken?.symbol || '' }} per order
            </div>
        </div>

        <!-- Summary -->
        <div class="compass-info-box mb-3" v-if="totalAmount > 0 && orderCount > 0">
            <div class="d-flex justify-content-between mb-1">
                <span class="text-muted small">Per Order</span>
                <span class="mono-num small">{{ formatAmount(amountPerOrder) }} {{ fromToken?.symbol }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1">
                <span class="text-muted small">Frequency</span>
                <span class="small">{{ selectedIntervalLabel }}</span>
            </div>
            <div class="d-flex justify-content-between">
                <span class="text-muted small">Total Duration</span>
                <span class="small">{{ durationText }}</span>
            </div>
        </div>

        <!-- Error -->
        <div class="alert alert-danger small py-2" v-if="error">
            {{ error }}
        </div>

        <!-- Success -->
        <div class="alert alert-success small py-2" v-if="successMessage">
            {{ successMessage }}
            <a v-if="explorerUrl" :href="explorerUrl" target="_blank" class="ms-1">View tx <i class="fas fa-external-link-alt"></i></a>
        </div>

        <!-- Submit -->
        <button
            class="compass-btn compass-btn--primary w-100"
            :disabled="!canSubmit || isSubmitting"
            @click="createDca"
        >
            <span v-if="isSubmitting">
                <i class="fas fa-spinner fa-spin me-1"></i> Creating DCA Order...
            </span>
            <span v-else-if="!isConnected">
                Connect Wallet
            </span>
            <span v-else>
                Create DCA Order
            </span>
        </button>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';
import TokenSelectorModal from './TokenSelectorModal.vue';

export default {
    name: 'DcaOrders',

    components: { TokenSelectorModal },

    props: {
        inputToken: { type: String, default: '' },
        outputToken: { type: String, default: '' },
    },

    data() {
        return {
            fromToken: this.inputToken ? { symbol: '', mint: this.inputToken } : { symbol: 'USDC', mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' },
            toToken: this.outputToken ? { symbol: '', mint: this.outputToken } : { symbol: 'SOL', mint: 'So11111111111111111111111111111111111111112' },
            showTokenSelector: false,
            tokenSelectorMode: 'from',
            totalAmount: null,
            interval: '1d',
            orderCount: 10,
            isSubmitting: false,
            error: null,
            successMessage: null,
            explorerUrl: null,
            intervalOptions: [
                { label: 'Hourly', value: '1h' },
                { label: 'Daily', value: '1d' },
                { label: 'Weekly', value: '1w' },
                { label: 'Monthly', value: '1M' },
            ],
        };
    },

    computed: {
        isConnected() {
            return !!window.wallet?.publicKey?.value;
        },

        amountPerOrder() {
            if (!this.totalAmount || !this.orderCount) return 0;
            return this.totalAmount / this.orderCount;
        },

        selectedIntervalLabel() {
            const opt = this.intervalOptions.find(o => o.value === this.interval);
            return opt ? opt.label : this.interval;
        },

        durationText() {
            if (!this.orderCount) return '';
            const multipliers = { '1h': 1, '1d': 24, '1w': 168, '1M': 720 };
            const totalHours = this.orderCount * (multipliers[this.interval] || 24);
            if (totalHours < 24) return `${totalHours} hours`;
            if (totalHours < 720) return `${Math.round(totalHours / 24)} days`;
            return `${(totalHours / 720).toFixed(1)} months`;
        },

        canSubmit() {
            return this.isConnected
                && this.fromToken?.symbol
                && this.toToken?.symbol
                && this.totalAmount > 0
                && this.orderCount >= 2
                && !this.isSubmitting;
        },
    },

    methods: {
        openSelector(mode) {
            this.tokenSelectorMode = mode;
            this.showTokenSelector = true;
        },

        handleTokenSelect({ token, mode }) {
            const mapped = {
                symbol: token.symbol,
                mint: token.mint,
                logoURI: token.logoURI || token.logo || null,
                name: token.name,
            };
            if (mode === 'from') {
                this.fromToken = mapped;
            } else {
                this.toToken = mapped;
            }
        },

        formatAmount(val) {
            if (val >= 1) return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
            return val.toPrecision(4);
        },

        async createDca() {
            this.error = null;
            this.successMessage = null;
            this.explorerUrl = null;
            this.isSubmitting = true;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();

                const result = await sdk.order.createDca(
                    this.totalAmount,
                    this.fromToken.symbol,
                    this.toToken.symbol,
                    'default',
                    {
                        interval: this.interval,
                        count: this.orderCount,
                    }
                );

                this.successMessage = `DCA order created! ${result.count} orders of ${this.formatAmount(result.amountPerOrder)} ${result.inputSymbol} → ${result.outputSymbol}`;
                this.explorerUrl = result.signature ? `https://solscan.io/tx/${result.signature}` : null;
                this.$emit('order-created', result);
            } catch (e) {
                this.error = e.message || 'Failed to create DCA order';
                console.error('[DcaOrders] createDca error:', e);
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
.dca-token-picker {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1rem;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.2s;
}
.dca-token-picker:hover {
    border-color: rgba(139, 92, 246, 0.5);
}
.dca-token-logo {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}
.dca-token-symbol {
    font-weight: 600;
    flex: 1;
}
.dca-chevron {
    opacity: 0.5;
}
</style>
